import { useState } from "react";
import { useLocation } from "wouter";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPassword() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "sent">("idle");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setStatus("loading");
    setError("");
    try {
      await fetch("/api/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setStatus("sent");
    } catch {
      setError("Network error. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <div className="min-h-screen bg-[#12122A] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1B1B3D] rounded-2xl border border-white/10 p-8 shadow-2xl">
        <button onClick={() => navigate("/login")} className="flex items-center gap-1.5 text-white/50 hover:text-white/80 text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Login
        </button>

        {status === "sent" ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Check your email</h1>
            <p className="text-white/60 text-sm mb-6">
              If an account exists for <span className="text-white font-medium">{email}</span>, a password reset link has been sent. Check your inbox and spam folder.
            </p>
            <p className="text-white/40 text-xs">The link expires in 1 hour.</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Forgot your password?</h1>
            <p className="text-white/60 text-sm mb-6">Enter your email address and we will send you a reset link.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  placeholder="you@example.com"
                  className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-violet-500"
                />
                {error && <p className="text-red-400 text-xs mt-1.5">{error}</p>}
              </div>
              <Button
                onClick={handleSubmit}
                disabled={status === "loading"}
                className="w-full rounded-xl font-bold py-2.5"
              >
                {status === "loading" ? "Sending…" : "Send Reset Link"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
